import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ClipboardCheck, CheckCircle2, XCircle, AlertCircle, RotateCcw } from "lucide-react";

/**
 * Componente Quiz - FASE 1
 * Sistema de testes com múltipla escolha, verificação de respostas,
 * pontuação e exibição de explicações
 */
const Quiz = ({ quizData }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);

  // Debug: verificar estrutura dos dados
  useEffect(() => {
    if (quizData) {
      console.log('[Quiz] Dados recebidos:', quizData);
      console.log('[Quiz] Primeira questão:', quizData[0]);
    }
  }, [quizData]);

  // Reset ao mudar de dados (novo vídeo)
  useEffect(() => {
    resetQuiz();
  }, [quizData]);

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setShowResults(false);
    setScore(0);
  };

  if (!quizData || quizData.length === 0) {
    return (
      <Card className="card">
        <CardContent className="p-8 text-center">
          <ClipboardCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Quiz não disponível para este vídeo</p>
        </CardContent>
      </Card>
    );
  }

  const currentQuestion = quizData[currentQuestionIndex];
  const totalQuestions = quizData.length;
  const isAnswered = selectedAnswers[currentQuestion.id] !== undefined;

  const handleSelectAnswer = (optionIndex) => {
    if (!showResults) {
      setSelectedAnswers({
        ...selectedAnswers,
        [currentQuestion.id]: optionIndex
      });
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = () => {
    // Calcular pontuação
    let correctCount = 0;
    quizData.forEach(question => {
      if (selectedAnswers[question.id] === question.correctAnswer) {
        correctCount++;
      }
    });
    
    setScore(correctCount);
    setShowResults(true);
  };

  const canSubmit = Object.keys(selectedAnswers).length === totalQuestions;
  const selectedAnswer = selectedAnswers[currentQuestion.id];
  const isCorrect = selectedAnswer === currentQuestion.correctAnswer;

  const getAnswerStatus = (optionIndex) => {
    if (!showResults) return null;
    
    if (optionIndex === currentQuestion.correctAnswer) {
      return 'correct';
    }
    
    if (optionIndex === selectedAnswer && optionIndex !== currentQuestion.correctAnswer) {
      return 'incorrect';
    }
    
    return null;
  };

  const getPercentage = () => {
    return Math.round((score / totalQuestions) * 100);
  };

  return (
    <Card className="card">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-orange-600" />
            Quiz de Verificação
          </CardTitle>
          <div className="flex gap-2">
            {showResults && (
              <Badge 
                variant={getPercentage() >= 70 ? "default" : "destructive"}
                className="text-sm"
              >
                {score}/{totalQuestions} corretas ({getPercentage()}%)
              </Badge>
            )}
            <Badge variant="secondary">
              Questão {currentQuestionIndex + 1}/{totalQuestions}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {!showResults ? (
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-100 rounded-lg p-4">
            <p className="text-sm text-orange-800">
              📝 Responda todas as questões e clique em "Finalizar Quiz" para ver seu resultado
            </p>
          </div>
        ) : (
          <div className={`border rounded-lg p-4 ${
            getPercentage() >= 70 
              ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200' 
              : 'bg-gradient-to-r from-red-50 to-orange-50 border-red-200'
          }`}>
            <div className="flex items-center gap-3">
              {getPercentage() >= 70 ? (
                <CheckCircle2 className="w-8 h-8 text-green-600 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-8 h-8 text-orange-600 flex-shrink-0" />
              )}
              <div>
                <p className={`font-semibold ${getPercentage() >= 70 ? 'text-green-900' : 'text-orange-900'}`}>
                  {getPercentage() >= 70 ? '✅ Parabéns! Você foi aprovado!' : '📚 Continue estudando!'}
                </p>
                <p className={`text-sm ${getPercentage() >= 70 ? 'text-green-700' : 'text-orange-700'}`}>
                  {getPercentage() >= 70 
                    ? 'Você demonstrou ótimo domínio do conteúdo.' 
                    : 'Revise os materiais e tente novamente.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Questão atual */}
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#111827' }}>
              {currentQuestion.question}
            </h3>
            
            {/* Opções */}
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => {
                const status = getAnswerStatus(index);
                const isSelected = selectedAnswer === index;
                
                return (
                  <button
                    key={index}
                    onClick={() => handleSelectAnswer(index)}
                    disabled={showResults}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      status === 'correct'
                        ? 'border-green-500 bg-green-50 text-green-900'
                        : status === 'incorrect'
                        ? 'border-red-500 bg-red-50 text-red-900'
                        : isSelected
                        ? 'border-blue-500 bg-blue-50 text-blue-900'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 text-gray-900'
                    } ${showResults ? 'cursor-default' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          status === 'correct'
                            ? 'border-green-500 bg-green-500'
                            : status === 'incorrect'
                            ? 'border-red-500 bg-red-500'
                            : isSelected
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300'
                        }`}>
                          {status === 'correct' && <CheckCircle2 className="w-4 h-4 text-white" />}
                          {status === 'incorrect' && <XCircle className="w-4 h-4 text-white" />}
                          {!status && isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                        </div>
                        <span 
                          className="font-medium"
                          style={{ 
                            color: status === 'correct' ? '#14532d' : 
                                   status === 'incorrect' ? '#7f1d1d' : 
                                   isSelected ? '#1e3a8a' : '#111827'
                          }}
                        >
                          {option}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Explicação (apenas após submissão) */}
          {showResults && (
            <div className={`border-l-4 rounded-lg p-4 ${
              isCorrect 
                ? 'border-green-500 bg-green-50' 
                : 'border-orange-500 bg-orange-50'
            }`}>
              <div className="flex items-start gap-2">
                <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                  isCorrect ? 'text-green-600' : 'text-orange-600'
                }`} />
                <div>
                  <p className={`font-semibold mb-1 ${
                    isCorrect ? 'text-green-900' : 'text-orange-900'
                  }`}>
                    {isCorrect ? 'Resposta Correta!' : 'Resposta Incorreta'}
                  </p>
                  <p className={`text-sm ${
                    isCorrect ? 'text-green-800' : 'text-orange-800'
                  }`}>
                    {currentQuestion.explanation}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navegação e controles */}
        <div className="flex flex-col gap-3">
          {/* Botões de navegação */}
          <div className="flex gap-3">
            <Button
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              variant="outline"
              className="flex-1"
            >
              Anterior
            </Button>
            
            <Button
              onClick={handleNext}
              disabled={currentQuestionIndex === totalQuestions - 1}
              variant="outline"
              className="flex-1"
            >
              Próxima
            </Button>
          </div>

          {/* Botão de submit/reset */}
          {!showResults ? (
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
            >
              <ClipboardCheck className="w-4 h-4 mr-2" />
              Finalizar Quiz
            </Button>
          ) : (
            <Button
              onClick={resetQuiz}
              variant="outline"
              className="w-full"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Refazer Quiz
            </Button>
          )}
        </div>

        {/* Progress indicators */}
        <div className="flex gap-1 justify-center flex-wrap">
          {quizData.map((question, idx) => {
            const answered = selectedAnswers[question.id] !== undefined;
            const isCurrent = idx === currentQuestionIndex;
            
            return (
              <div
                key={question.id}
                onClick={() => setCurrentQuestionIndex(idx)}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold cursor-pointer transition-all ${
                  isCurrent
                    ? 'bg-blue-600 text-white scale-110'
                    : answered
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                {idx + 1}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default Quiz;
